// Utility / formatting functions shared across the app

export const formatNotesWithBullets = (notes) => {
  if (!notes || !notes.trim()) return '';
  return notes
    .split('\n')
    .filter(line => line.trim())
    .map(line => `• ${line.trim()}`)
    .join('\n');
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  // Arrival dates are stored as plain date strings (YYYY-MM-DD) — no timezone conversion needed
  // For datetime strings, convert to AEST before extracting the date
  const str = dateString.toString();
  if (str.length === 10 && str.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Plain date string — parse directly, no timezone shift
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  }
  // Datetime string — convert to AEST
  const dateStr = str.endsWith('Z') ? str : str + 'Z';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    timeZone: 'Australia/Brisbane'
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const value = String(dateString);
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = dateString instanceof Date ? dateString : new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : hasTimezone ? value : `${value}Z`
  );
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Brisbane'
  });
};

export const getDayOfWeek = (dateString) => {
  if (!dateString) return '';
  const str = dateString.toString();
  // Plain date string — parse without timezone shift
  if (str.length === 10 && str.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = str.split('-').map(Number);
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return days[new Date(year, month - 1, day).getDay()];
  }
  // Datetime — use AEST
  const dateStr = str.endsWith('Z') ? str : str + 'Z';
  const d = new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return days[d.getDay()];
};

export const formatDateWithDay = (dateString) => {
  if (!dateString) return '';
  const day = getDayOfWeek(dateString);
  const formattedDate = formatDate(dateString);
  return `${day} ${formattedDate}`;
};

export const getLocationDisplay = (area, detail) => {
  if (!area) return '';
  return detail ? `${area} · ${detail}` : area;
};

export const getLocationChipColor = (area) => {
  switch (area) {
    case 'Ready for Collection':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'In Cremation':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Refrigeration':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'Cremation Queue':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Keepsakes Prep':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Urn Assembled':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getRowAlert = (pet) => {
  const arrivalDate = new Date(pet.arrival_date);
  const daysSinceArrival = Math.floor((new Date() - arrivalDate) / (1000 * 60 * 60 * 24));
  if (pet.location_area === 'Refrigeration' && daysSinceArrival > 5) {
    return 'bg-amber-50 border-l-4 border-amber-400';
  }
  if (pet.location_area === 'In Cremation') {
    return 'bg-blue-50 border-l-4 border-blue-400';
  }
  if (pet.status === 'In Progress') {
    return 'bg-yellow-50 border-l-4 border-yellow-400';
  }
  return '';
};

export const formatDatetimeForInput = (datetimeValue) => {
  if (!datetimeValue) return '';
  const date = new Date(datetimeValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const parseUTCDatetime = (datetimeValue) => {
  if (!datetimeValue) return null;
  const dateStr = datetimeValue.toString();
  if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
    return new Date(dateStr + 'Z');
  }
  return new Date(dateStr);
};

export const getTodayInAEST = () => {
  // Townsville uses AEST (UTC+10) — no daylight saving
  const now = new Date();
  const aestDate = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  const year = aestDate.getFullYear();
  const month = String(aestDate.getMonth() + 1).padStart(2, '0');
  const day = String(aestDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isPrepaymentApproaching = (prepaymentDate) => {
  if (!prepaymentDate) return false;
  // Use AEST timezone for date comparison (Townsville is AEST UTC+10)
  const now = new Date();
  const todayAEST = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  todayAEST.setHours(0, 0, 0, 0);  // Start of today in AEST
  // Parse prepayment date — add Z if no timezone to treat as UTC
  const dateStr = prepaymentDate.toString();
  const paymentDate = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
  const paymentDateAEST = new Date(paymentDate.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  paymentDateAEST.setHours(0, 0, 0, 0);  // Start of that day in AEST
  const diffDays = Math.ceil((paymentDateAEST - todayAEST) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= 3;
};

export const hasAppointments = (pet) => {
  return !!(
    (pet.ashes_collection_datetime && !pet.ashes_collection_completed) ||
    (pet.prepayment_date && !pet.prepayment_completed && !pet.pet_in_care_sms) ||
    (pet.viewing_room_datetime) ||
    (pet.showroom_visit_datetime && !pet.showroom_visit_completed) ||
    (pet.other_appointment_datetime && !pet.other_appointment_completed) ||
    (pet.pet_delivery_datetime && !pet.pet_delivery_completed) ||
    (pet.pet_dropoff_datetime && !pet.pet_dropoff_completed) ||
    (pet.home_collection_datetime && !pet.home_collection_completed) ||
    (pet.vet_dropoff_datetime && !pet.vet_dropoff_completed)
  );
};

export const getAppointmentSummary = (pet) => {
  const appointments = [];
  if (pet.ashes_collection_datetime && !pet.ashes_collection_completed) {
    const aptDate = new Date(pet.ashes_collection_datetime);
    appointments.push(`🏺 Ashes Collection: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.prepayment_date && !pet.prepayment_completed && !pet.pet_in_care_sms) {
    const aptDate = new Date(pet.prepayment_date);
    appointments.push(`⭐️ Prepayment: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.viewing_room_datetime) {
    const aptDate = new Date(pet.viewing_room_datetime);
    appointments.push(`🕯️ Viewing Room: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.showroom_visit_datetime && !pet.showroom_visit_completed) {
    const aptDate = new Date(pet.showroom_visit_datetime);
    appointments.push(`🚙 Showroom Visit: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.other_appointment_datetime && !pet.other_appointment_completed) {
    const aptDate = new Date(pet.other_appointment_datetime);
    appointments.push(`🚐 Vet Collection: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.pet_delivery_datetime && !pet.pet_delivery_completed) {
    const aptDate = new Date(pet.pet_delivery_datetime);
    appointments.push(`🚚 Ashes Delivery: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.pet_dropoff_datetime && !pet.pet_dropoff_completed) {
    const aptDate = new Date(pet.pet_dropoff_datetime);
    appointments.push(`🦮 Client Drop off: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.home_collection_datetime && !pet.home_collection_completed) {
    const aptDate = new Date(pet.home_collection_datetime);
    appointments.push(`🏡 Home Collection: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  if (pet.vet_dropoff_datetime && !pet.vet_dropoff_completed) {
    const aptDate = new Date(pet.vet_dropoff_datetime);
    appointments.push(`🏥 Vet Drop off: ${aptDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
  }
  const petName = pet.pet_name?.trim() ? pet.pet_name : 'Pet';
  const clientName = pet.client_name?.trim() ? ` (${pet.client_name})` : '';
  if (appointments.length === 1) {
    return `${petName}${clientName}: ${appointments[0]}`;
  }
  return `${petName}${clientName} has ${appointments.length} appointments:\n${appointments.join('\n')}`;
};


// Partial-payment summary: total paid across recorded installments vs total cost.
export const getPaymentSummary = (pet) => {
  const payments = Array.isArray(pet?.payments) ? pet.payments : [];
  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p?.amount) || 0), 0);
  const totalCost = parseFloat(pet?.total_cost) || 0;
  const owing = Math.max(totalCost - totalPaid, 0);
  return {
    payments,
    hasPayments: payments.length > 0,
    totalPaid: Math.round(totalPaid * 100) / 100,
    totalCost: Math.round(totalCost * 100) / 100,
    owing: Math.round(owing * 100) / 100,
    isPartial: payments.length > 0 && totalCost > 0 && totalPaid < totalCost,
    isFullyPaid: totalCost > 0 && totalPaid >= totalCost,
  };
};
